// @ts-nocheck
import {useCallback} from "react";
import {useNavigate} from "react-router-dom";
import {BaseButton, BaseCarousel, SectionHeader} from "@/components";
import {useAuthStore} from "@/store/useAuth.store";
import fallbackImage from "../../../../../assets/images/fallback-image.svg";
import crownIcon from "../../../../../assets/images/icon-games-crown.svg";
import useGetPublicLotteryGames from "../../../../private/Lottery/hooks/useGetPublicLotteryGames";

const containerCls =
  "depth-section-warm section-bleed my-0 rounded-none flex flex-col items-center justify-center gap-10 overflow-hidden py-14 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),inset_0_-1px_0_rgba(0,0,0,0.4)] max-[780px]:gap-8 max-[780px]:py-10";

const crownIconCls = "inline-block w-[1em] h-[1em] object-contain shrink-0";

const btnWrapperCls = "flex items-center justify-center w-full";

const allGamesBtnStyle = {
  "--btn-outline-color": "var(--color-lottery)",
  "--btn-outline-fill": "var(--color-lottery)",
  "--btn-outline-text-hover": "var(--color-text-inverse)",
  "--btn-outline-glow": "rgba(255, 3, 96, 0.45)",
} as React.CSSProperties;

const SKELETON_KEYS = ["skel-1", "skel-2", "skel-3", "skel-4", "skel-5", "skel-6", "skel-7", "skel-8"];

/* ── Premium game card ─────────────────────────────────────── */
const GameCard = ({game, onClick}) => (
  <div
    onClick={onClick}
    className="
      group relative shrink-0 cursor-pointer select-none
      w-[180px] h-[180px] rounded-2xl overflow-hidden
      bg-[rgba(255,255,255,0.04)]
      border border-[rgba(255,255,255,0.08)]
      shadow-[0_8px_32px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.08)]
      transition-all duration-300
      hover:-translate-y-2
      hover:shadow-[0_20px_48px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.14),inset_0_1px_0_rgba(255,255,255,0.16)]
      hover:border-[rgba(255,255,255,0.15)]
      max-[780px]:w-[136px] max-[780px]:h-[136px] max-[780px]:rounded-xl
    "
  >
    {/* Bottom gradient overlay with name */}
    <div className="
      absolute bottom-0 left-0 right-0 z-10 px-2 pb-2.5 pt-6
      bg-[linear-gradient(to_top,rgba(0,0,0,0.82)_0%,transparent_100%)]
    ">
      <p className="
        m-0 text-[11px] font-bold text-white text-center leading-tight
        overflow-hidden [display:-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical]
        max-[780px]:text-[10px]
      ">
        {game.name}
      </p>
    </div>

    {/* Game logo */}
    <img
      src={game.logo}
      alt={game.name}
      onError={e => { e.target.onerror = null; e.target.src = fallbackImage; }}
      className="w-full h-full object-contain block p-3 transition-transform duration-300 group-hover:scale-105 max-[780px]:p-2"
    />

    {/* Bottom shine on hover */}
    <div className="
      absolute bottom-0 left-0 right-0 h-[40%]
      bg-[linear-gradient(to_top,rgba(255,255,255,0.04)_0%,transparent_100%)]
      opacity-0 group-hover:opacity-100 transition-opacity duration-300
    " />

    {/* Subtle corner accent */}
    <div className="
      absolute top-0 right-0 w-8 h-8
      bg-[linear-gradient(135deg,rgba(255,255,255,0.12)_0%,transparent_60%)]
      rounded-bl-2xl
    " />
  </div>
);

/* ── Skeleton card ─────────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="
    shrink-0 w-[180px] h-[180px] rounded-2xl
    bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.06)]
    animate-pulse max-[780px]:w-[136px] max-[780px]:h-[136px] max-[780px]:rounded-xl
  ">
    <div className="w-full h-full flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[rgba(255,255,255,0.1)] border-t-[rgba(255,3,96,0.6)] rounded-full animate-spin" />
    </div>
  </div>
);

/* ── Main component ────────────────────────────────────────── */
const LotteryGamesCarousel = () => {
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);

  const {games, isPending, error} = useGetPublicLotteryGames();

  const handleGameClick = useCallback(
    gameId => {
      if (authStatus === "authenticated") {
        navigate(`/lottery?gameId=${gameId}`);
      } else {
        handleShowAuthModal();
      }
    },
    [authStatus, navigate, handleShowAuthModal]
  );

  const handleViewAll = useCallback(() => {
    if (authStatus === "authenticated") {
      navigate("/lottery");
    } else {
      handleShowAuthModal();
    }
  }, [authStatus, navigate, handleShowAuthModal]);

  if (error || (!isPending && games.length === 0)) {
    return null;
  }

  const carouselItems = isPending
    ? SKELETON_KEYS.map(key => <SkeletonCard key={key} />)
    : games.map(game => (
        <GameCard
          key={game.uniqueId}
          game={game}
          onClick={() => handleGameClick(game.id)}
        />
      ));

  return (
    <div
      id="lotteryGamesCarousel"
      className={containerCls}
    >
      <SectionHeader
        icon={<img src={crownIcon} alt="" className={crownIconCls} />}
        eyebrow="Loterías"
        title="Juega, pega y corona"
        highlight="corona"
        subtitle="Las loterías que más pagan, cada hora"
        subtitleIcon={<span aria-hidden="true">$</span>}
        align="center"
        variant="hero"
      />

      <div className="w-full">
        <BaseCarousel
          arrayItems={carouselItems}
          secondsPerItem={3}
          pauseOnHover
          enableDrag
          innerClassName="flex items-center gap-5 [&>:last-child]:mr-5 hover:[animation-play-state:paused] max-[780px]:gap-3 max-[780px]:[&>:last-child]:mr-3"
        />
      </div>

      <div className={btnWrapperCls}>
        <BaseButton
          variant="outline"
          size="md"
          onClick={handleViewAll}
          style={allGamesBtnStyle}
          className="min-w-[164px]"
        >
          Ir a todos
        </BaseButton>
      </div>
    </div>
  );
};

export default LotteryGamesCarousel;
